import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarIcon, ChevronIcon, CloseIcon } from "./Icons";
import { datePresets, iso, rangeLabel } from "../utils";

/** Compact "📅 This month ▾" chip with a popover of presets and a custom From / To. */
export default function DateRangePicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value?.from || "");
  const [to, setTo] = useState(value?.to || "");
  const [error, setError] = useState("");
  const ref = useRef(null);
  const presets = useMemo(() => datePresets(), [open]); // recompute when opened (date may change)

  useEffect(() => {
    setFrom(value?.from || "");
    setTo(value?.to || "");
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const active = value && (value.from || value.to);

  const applyCustom = () => {
    if (from && to && from > to) {
      setError("From date must be before To date.");
      return;
    }
    if (!from && !to) {
      onChange(null);
    } else {
      onChange({ from, to, label: "Custom" });
    }
    setError("");
    setOpen(false);
  };

  return (
    <div className="bdai-range" ref={ref}>
      <button
        type="button"
        className={`bdai-range-chip ${active ? "bdai-range-chip-on" : ""}`}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Date range for your questions"
      >
        <CalendarIcon size={14} />
        <span className="bdai-range-text">{rangeLabel(value)}</span>
        <ChevronIcon size={13} className={open ? "bdai-rot" : ""} />
      </button>
      {active && (
        <button type="button" className="bdai-range-clear" onClick={() => onChange(null)} title="Clear date range" aria-label="Clear date range">
          <CloseIcon size={12} />
        </button>
      )}

      {open && (
        <div className="bdai-range-pop" role="dialog" aria-label="Choose date range">
          <div className="bdai-range-section">Quick ranges</div>
          <div className="bdai-range-grid">
            <button
              type="button"
              className={`bdai-range-preset ${!active ? "bdai-active" : ""}`}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              All time
            </button>
            {presets.map((p) => (
              <button
                type="button"
                key={p.key}
                className={`bdai-range-preset ${value?.label === p.label ? "bdai-active" : ""}`}
                onClick={() => {
                  onChange({ from: p.from, to: p.to, label: p.label });
                  setOpen(false);
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="bdai-range-section">Custom</div>
          <div className="bdai-range-custom">
            <label className="bdai-field">
              <span>From</span>
              <input type="date" value={from} max={to || iso(new Date(2100, 0, 1))} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="bdai-field">
              <span>To</span>
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
          {error && <div className="bdai-range-error">{error}</div>}
          <div className="bdai-range-actions">
            <button type="button" className="bdai-btn bdai-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="bdai-btn bdai-btn-primary" onClick={applyCustom}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
